import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  UserCog,
  Save,
  Edit3,
  Building,
  Mail,
  Phone,
  MapPin,
  UserCircle,
  UploadCloud,
  Image as ImageIcon,
  Lock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import {
  updateUserProfile,
  checkCompanyNameExists
} from "../store/auth/authActions";
import ResetPasswordModal from "../components/Modal/ResetPasswordModal";
import { Formik } from "formik";
import * as Yup from "yup";

const profileValidationSchema = Yup.object().shape({
  companyName: Yup.string()
    .trim()
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name must be 100 characters or less")
    .required("Company name is required"),
  contactName: Yup.string()
    .trim()
    .min(4, "Contact name must be at least 4 characters")
    .max(50, "Contact name must be 50 characters or less")
    .matches(
      /^[A-Za-z\s]+$/,
      "Contact name can only contain alphabets and spaces"
    )
    .required("Contact name is required"),

  postalCode: Yup.string()
    .trim()
    .matches(/^[0-9-]+$/, "Postal code can only contain numbers and hyphens")
    .min(4, "Postal code must be at least 4 characters")
    .max(10, "Postal code must be at most 10 characters")
    .notRequired()
    .nullable(),

  phone: Yup.string()
    .trim()
    .matches(
      /^(\+1\s?)?(\([2-9][0-9]{2}\)|[2-9][0-9]{2})[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/,
      "Enter a valid US phone number (e.g., 555-123-4567 or +1 555 123 4567)"
    )
    .notRequired()
    .nullable(),
  address: Yup.string()
    .trim()
    .max(200, "Address must be 200 characters or less")
    .notRequired()
    .nullable(),
  bio: Yup.string()
    .trim()
    .max(500, "Bio must be 500 characters or less")
    .notRequired()
    .nullable()
});

const SellerProfilePage = () => {
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    user,
    isUserUpdateLoading,
    isCheckLoading,
    companyExists
  } = useSelector((state) => state.auth);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState(user?.logoUrl || null);
  const [logoFile, setLogoFile] = useState(null);
  const [isLogoChanged, setIsLogoChanged] = useState(false);

  // Company name validation states
  const [originalCompanyName, setOriginalCompanyName] = useState("");
  const [companyCheckStatus, setCompanyCheckStatus] = useState(null);
  const [companyNameError, setCompanyNameError] = useState("");
  const debounceTimerRef = useRef(null);
  const [touchedCompanyName, setTouchedCompanyName] = useState(false);

  useEffect(() => {
    if (user?.companyName) {
      setOriginalCompanyName(user.companyName);
    }
  }, [user?.companyName]);

  const checkCompanyName = useCallback(
    (companyName) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // If company name is same as original, don't check
      if (companyName.trim() === originalCompanyName.trim()) {
        setCompanyCheckStatus(null);
        setCompanyNameError("");
        return;
      }

      // If empty, show required error
      if (!companyName.trim()) {
        setCompanyCheckStatus(null);
        setCompanyNameError("Company name is required");
        return;
      }

      setCompanyCheckStatus("checking");
      setCompanyNameError("");

      debounceTimerRef.current = setTimeout(() => {
        if (
          companyName.trim() &&
          companyName.trim() !== originalCompanyName.trim()
        ) {
          dispatch(checkCompanyNameExists(companyName.trim()));
        }
      }, 700);
    },
    [dispatch, originalCompanyName]
  );

  // Handle company name check results
  useEffect(() => {
    if (touchedCompanyName && isEditing) {
      if (companyExists === true) {
        setCompanyCheckStatus("taken");
        setCompanyNameError(
          "This company name is already registered. Please choose a different name."
        );
      } else if (companyExists === false && !isCheckLoading) {
        setCompanyCheckStatus("available");
        setCompanyNameError("");
      }
    }
  }, [companyExists, isCheckLoading, touchedCompanyName, isEditing]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleLogoChange = useCallback(
    (e, setFieldValue) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 2 * 1024 * 1024) {
          toast({
            variant: "destructive",
            title: "File too large",
            description: "Logo image must be under 2MB."
          });
          return;
        }
        if (!file.type.startsWith("image/")) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: "Please upload an image file."
          });
          return;
        }

        setLogoFile(file);
        setIsLogoChanged(true);

        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          setLogoPreview(result);
          setFieldValue("logoUrl", result);
        };
        reader.readAsDataURL(file);
      }
    },
    [toast]
  );

  const handleCompanyNameChange = useCallback(
    (e, handleChange) => {
      const newValue = e.target.value;
      handleChange(e);
      setTouchedCompanyName(true);
      checkCompanyName(newValue);
    },
    [checkCompanyName]
  );

  const handleSaveChanges = async (values, { setSubmitting, resetForm }) => {
    // Check if company name has changed and if it's taken
    if (
      values.companyName.trim() !== originalCompanyName.trim() &&
      companyExists === true
    ) {
      toast({
        title: "Company Name Unavailable",
        description:
          "This company name is already registered. Please choose a different name.",
        variant: "destructive"
      });
      setSubmitting(false);
      return;
    }

    // Check if still checking company name
    if (
      values.companyName.trim() !== originalCompanyName.trim() &&
      (isCheckLoading || companyCheckStatus === "checking")
    ) {
      toast({
        title: "Please Wait",
        description: "Still checking company name availability...",
        variant: "destructive"
      });
      setSubmitting(false);
      return;
    }

    try {
      const { email, ...dataToUpdate } = values;

      if (!isLogoChanged) {
        delete dataToUpdate.logoUrl;
      }

      if (dataToUpdate.contactName) {
        dataToUpdate.name = dataToUpdate.contactName;
      }

      const resultAction = await dispatch(updateUserProfile(dataToUpdate));

      if (updateUserProfile.fulfilled.match(resultAction)) {
        setIsEditing(false);
        setLogoFile(null);
        setIsLogoChanged(false);
        setTouchedCompanyName(false);
        setCompanyCheckStatus(null);
        setCompanyNameError("");

        const updatedUser = resultAction.payload;

        // Update original company name to new value
        if (updatedUser.companyName) {
          setOriginalCompanyName(updatedUser.companyName);
        }

        const newFormValues = {
          companyName: updatedUser.companyName || "",
          contactName: updatedUser.name || "",
          email: updatedUser.email || "",
          phone: updatedUser.phone || "",
          address: updatedUser.address || "",
          bio: updatedUser.bio || "",
          logoUrl: updatedUser.logoUrl || "",
          postalCode: updatedUser.postalCode || ""
        };

        resetForm({ values: newFormValues });

        if (updatedUser.logoUrl) {
          setLogoPreview(updatedUser.logoUrl);
        }

        toast({
          title: "Success",
          description: "Profile updated successfully!",
          variant: "default"
        });
      } else {
        throw new Error(resultAction.payload || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update Failed",
        description:
          error.message || "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = (resetForm, initialValues) => {
    setIsEditing(false);
    setLogoPreview(user?.logoUrl || null);
    setLogoFile(null);
    setIsLogoChanged(false);
    setTouchedCompanyName(false);
    setCompanyCheckStatus(null);
    setCompanyNameError("");
    resetForm({ values: initialValues });
  };

  const getCompanyNameBorderClass = (values) => {
    if (!isEditing || !touchedCompanyName) return "";

    // If same as original, no special styling
    if (values.companyName.trim() === originalCompanyName.trim()) return "";

    if (companyCheckStatus === "taken")
      return "border-red-500 focus:ring-red-500";
    if (companyCheckStatus === "available")
      return "border-green-500 focus:ring-green-500";
    if (companyCheckStatus === "checking")
      return "border-blue-400 focus:ring-blue-400";
    return "";
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.5 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  useEffect(() => {
    if (user?.logoUrl && !logoPreview) {
      setLogoPreview(user.logoUrl);
    }
  }, [user?.logoUrl, logoPreview]);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () =>
            navigate("/dashboard/profile", { replace: true })
        })
      );
    } else if (user && user.role !== "seller") {
      toast({
        title: "Access Denied",
        description: "This page is for sellers. Redirecting...",
        variant: "destructive"
      });
      navigate(user.role === "buyer" ? "/profile" : "/");
    }
  }, [isAuthenticated, user, navigate, toast, dispatch]);

  if (!isAuthenticated || !user || user.role !== "seller") {
    return (
      <div className="w-full py-20 text-center flex flex-col justify-center items-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-primary mb-4">
            Accessing Seller Profile
          </h1>
          <p className="text-muted-foreground">
            {!isAuthenticated
              ? "Please log in as Seller to continue."
              : "Verifying account..."}
          </p>
        </motion.div>
      </div>
    );
  }

  const initialFormValues = {
    companyName: user.companyName || "",
    contactName: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    address: user.address || "",
    bio: user.bio || "",
    logoUrl: user.logoUrl || "",
    postalCode: user?.postalCode || ""
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={profileValidationSchema}
      onSubmit={handleSaveChanges}
      enableReinitialize
      key={user?.uid}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue
      }) => (
        <motion.div
          className="space-y-8 w-full max-w-3xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-accent">
              Seller Profile
            </h1>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <motion.div variants={itemVariants}>
              <Card className="shadow-xl border-l-4 border-accent">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="h-6 w-6 text-accent" /> Business
                    Information
                  </CardTitle>
                  <CardDescription>
                    Manage your public-facing seller profile details. This
                    information will be visible to buyers.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div className="space-y-2">
                    <Label htmlFor="logoUpload" className="flex items-center">
                      <ImageIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      Company Logo
                    </Label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-lg border border-dashed border-muted flex items-center justify-center bg-secondary/30 overflow-hidden">
                        {logoPreview || values.logoUrl ? (
                          <img
                            src={logoPreview || values.logoUrl}
                            alt="Company Logo Preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building className="h-10 w-10 text-muted-foreground" />
                        )}
                      </div>
                      {isEditing && (
                        <div>
                          <Input
                            id="logoUpload"
                            name="logoUrl"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoChange(e, setFieldValue)}
                            className="hidden"
                          />
                          <Label
                            htmlFor="logoUpload"
                            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                          >
                            <UploadCloud className="mr-2 h-4 w-4" /> Change Logo
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Max 2MB. Recommended: Square image.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="companyName"
                        className="flex items-center text-sm font-medium"
                      >
                        <Building className="mr-2 h-4 w-4 text-muted-foreground" />
                        Company Name
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="companyName"
                          name="companyName"
                          value={values.companyName}
                          onChange={(e) =>
                            handleCompanyNameChange(e, handleChange)
                          }
                          onBlur={handleBlur}
                          disabled={!isEditing}
                          placeholder="Your Company LLC"
                          className={`pr-10 ${getCompanyNameBorderClass(
                            values
                          )}`}
                        />
                        {isEditing &&
                          values.companyName.trim() !==
                            originalCompanyName.trim() && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              {companyCheckStatus === "checking" && (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                              )}
                              {companyCheckStatus === "available" && (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              )}
                              {companyCheckStatus === "taken" && (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          )}
                      </div>
                      <AnimatePresence mode="wait">
                        {isEditing &&
                          values.companyName.trim() !==
                            originalCompanyName.trim() && (
                            <>
                              {!values.companyName.trim() ? (
                                <motion.div
                                  key="empty"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="flex items-start gap-1.5 text-xs text-amber-600"
                                >
                                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Company name is required</span>
                                </motion.div>
                              ) : companyCheckStatus === "available" ? (
                                <motion.div
                                  key="available"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="flex items-start gap-1.5 text-xs text-green-600 font-medium"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                  <span>Company name is available</span>
                                </motion.div>
                              ) : companyCheckStatus === "taken" ? (
                                <motion.div
                                  key="taken"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="flex items-start gap-1.5 text-xs text-red-500 font-medium"
                                >
                                  <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                  <span>{companyNameError}</span>
                                </motion.div>
                              ) : null}
                            </>
                          )}
                        {errors.companyName &&
                          touched.companyName &&
                          !isEditing && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="flex items-start gap-1.5 text-xs text-red-500"
                            >
                              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              <span>{errors.companyName}</span>
                            </motion.div>
                          )}
                      </AnimatePresence>
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="contactName"
                        className="flex items-center py-1"
                      >
                        <UserCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                        Contact Person
                      </Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={values.contactName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isEditing}
                        placeholder="John Doe"
                      />
                      <AnimatePresence>
                        {errors.contactName && touched.contactName && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="text-xs text-red-400"
                          >
                            {errors.contactName}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <Label htmlFor="email" className="flex items-center py-1">
                        <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={values.email}
                        disabled
                        placeholder="yourcompany@example.com"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="phone" className="flex items-center py-1">
                        <Phone className="mr-2 h-4 w-4 text-muted-foreground" />
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isEditing}
                        placeholder="(123) 456-7890"
                      />
                      <AnimatePresence>
                        {errors.phone && touched.phone && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="text-xs text-red-400"
                          >
                            {errors.phone}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <Label
                        htmlFor="postalCode"
                        className="flex items-center py-1"
                      >
                        <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                        Postal Code
                      </Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isEditing}
                        placeholder="12312"
                      />
                      <AnimatePresence>
                        {errors.postalCode && touched.postalCode && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="text-xs text-red-400"
                          >
                            {errors.postalCode}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="address" className="flex items-center py-1">
                      <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                      Business Address
                    </Label>
                    <Textarea
                      id="address"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditing}
                      placeholder="123 Main St, Anytown, USA"
                      rows={3}
                    />
                    <AnimatePresence>
                      {errors.address && touched.address && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-xs text-red-400"
                        >
                          {errors.address}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="bio" className="flex items-center py-1">
                      <Edit3 className="mr-2 h-4 w-4 text-muted-foreground" />
                      Short Bio / About Your Business
                    </Label>
                    <Textarea
                      id="bio"
                      name="bio"
                      value={values.bio}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditing}
                      placeholder="Specializing in quality used parts for European vehicles..."
                      rows={4}
                    />
                    <AnimatePresence>
                      {errors.bio && touched.bio && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-xs text-red-400"
                        >
                          {errors.bio}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="flex justify-between items-center">
                    {isEditing ? (
                      <div className="flex gap-2 pt-4">
                        <Button
                          type="submit"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={
                            isSubmitting ||
                            isUserUpdateLoading ||
                            (values.companyName.trim() !==
                              originalCompanyName.trim() &&
                              (isCheckLoading ||
                                companyCheckStatus === "checking" ||
                                companyCheckStatus === "taken"))
                          }
                        >
                          {(isSubmitting || isUserUpdateLoading) && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          <Save className="mr-2 h-4 w-4" /> Save Changes
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isSubmitting || isUserUpdateLoading}
                          onClick={() =>
                            handleCancelEdit(resetForm, initialFormValues)
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="mt-2"
                      >
                        <Edit3 className="mr-2 h-4 w-4" /> Edit Profile
                      </Button>
                    )}

                    {user?.authProvider !== "google" && (
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-3"
                        onClick={() => setResetModalOpen(true)}
                      >
                        <Lock className="mr-2 h-4 w-4" /> Change Password
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              <ResetPasswordModal
                open={resetModalOpen}
                onClose={() => setResetModalOpen(false)}
              />
            </motion.div>
          </form>
        </motion.div>
      )}
    </Formik>
  );
};

export default SellerProfilePage;
